import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loading } from '@/components/loading';
import { Center } from '@/components/center';
import { PendingInvitationsBox } from '@/components/pending-invitations-box';
import { AppLayout } from '@/components/app-layout';
import { useMeInvitations } from '@/hooks/use-me-invitations';
import { useSessionQuery } from '@/api/login';
import {
  useAcceptOrganisationInvitationMutation,
  useDeclineOrganisationInvitationMutation,
} from '@/api/user';
import { useAcceptShareMutation, useDeclineShareMutation } from '@/api/share';
import { useToast } from '@kinesis/bungle';
import { launch, sharedPathnameFor } from '@/services/launch';
import { takeEmailedShare } from '@/services/emailed-link';
import { InvitationError } from '@/components/invitation-error';

const Invitations = () => {
  const toast = useToast('globalTop');
  const { data: session, isSuccess, isLoading } = useSessionQuery();

  const { data: invitations, isLoading: invitationsLoading } =
    useMeInvitations();

  // An orphan — no organisation yet — is shown organisation rows only.
  // Accepting one is how their registration finishes; accepting a share
  // finishes nothing and would send them on into a product without an
  // organisation. Their shares wait for the landing, which launch reaches
  // once the session is finished. Everyone else sees every row.
  const shown = useMemo(
    () =>
      session?.orphan
        ? invitations.filter((i) => i.namespace === 'organisation')
        : invitations,
    [invitations, session?.orphan],
  );

  const [organisationInvitationAccept, { isLoading: isAcceptingDiplomat }] =
    useAcceptOrganisationInvitationMutation();
  const [organisationInvitationDecline, { isLoading: isDecliningDiplomat }] =
    useDeclineOrganisationInvitationMutation();
  const [shareAccept, { isLoading: isAcceptingShare }] =
    useAcceptShareMutation();
  const [shareDecline, { isLoading: isDecliningShare }] =
    useDeclineShareMutation();
  const [showInvitationError, setShowInvitationError] = useState(false);

  const onAcceptInvitation = useCallback(
    async (token: string) => {
      const invitation = invitations.find((i) => i.token === token);

      // A disclosure share: accepting opens the disclosure itself, rather
      // than joining anything.
      if (invitation?.namespace === 'disclosure') {
        try {
          const accepted = await shareAccept(token).unwrap();
          const pathname = sharedPathnameFor(accepted.object);
          if (pathname) {
            // Leaving the ladder without launch: the held token ends here.
            takeEmailedShare();
            window.location.assign(pathname);
          }
        } catch (e) {
          toast('Error accepting share.', {
            variant: 'error',
            duration: 6000,
          });
        }
        return;
      }

      // An organisation row is accepted through friend by its token; whether
      // the organisation's SSO mandate refuses this login is answered here.
      try {
        await organisationInvitationAccept(token).unwrap();
      } catch (e) {
        if ((e as any)?.data?.code === 'invitation-sso-mandated') {
          return setShowInvitationError(true);
        }
        toast('Error accepting invitation.', {
          variant: 'error',
          duration: 6000,
        });
      }
    },
    [organisationInvitationAccept, shareAccept, invitations, toast],
  );

  const onDeclineInvitation = useCallback(
    async (token: string) => {
      const invitation = invitations.find((i) => i.token === token);
      try {
        if (invitation?.namespace === 'disclosure') {
          await shareDecline(token).unwrap();
        } else {
          await organisationInvitationDecline(token).unwrap();
        }
      } catch (e) {
        toast('Error declining invitation.', {
          variant: 'error',
          duration: 6000,
        });
      }
    },
    [organisationInvitationDecline, shareDecline, invitations, toast],
  );
  const navigate = useNavigate();

  const handleDone = useCallback(() => {
    if (session?.orphan) {
      return navigate('/registration/complete');
    } else {
      launch(session?.target, true);
    }
  }, [navigate, session?.orphan, session?.target]);

  // Nothing to show — the rows were all shares and this is an orphan — is
  // the same as Done: the list has no job here.
  const nothingToShow =
    !isLoading && isSuccess && !invitationsLoading && shown.length === 0;
  useEffect(() => {
    if (nothingToShow) {
      handleDone();
    }
  }, [nothingToShow, handleDone]);

  if (!isLoading && isSuccess) {
    if (!invitationsLoading && shown.length > 0) {
      return (
        <AppLayout>
          <>
            {showInvitationError && (
              <InvitationError onClose={() => setShowInvitationError(false)} />
            )}
            <Center>
              <PendingInvitationsBox
                onAccept={onAcceptInvitation}
                onDecline={onDeclineInvitation}
                onDone={handleDone}
                invitations={shown}
                isLoading={
                  isAcceptingDiplomat ||
                  isDecliningDiplomat ||
                  isAcceptingShare ||
                  isDecliningShare
                }
              />
            </Center>
          </>
        </AppLayout>
      );
    } else {
      return <Loading />;
    }
  }

  return null;
};

export { Invitations };
